import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import {
	Button,
	StandardButton,
	ModalDialog,
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
} from '@atlassian/jira-portfolio-3-common';
import {
	getPlanId,
	getScenarioId,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/util/urls';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { buildRemoveIssuePath } from '@atlassian/jira-portfolio-3-portfolio/src/common/window';
import { useRouterActions } from '@atlassian/jira-router';
import messages from './messages';
import type { Props } from './types';

const RemoveIssuesDialog = ({ toggleModalDialog, bulkActionSuccess, removeFromPlan }: Props) => {
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();
	const onClickRemovedIssue = () => {
		const planId = getPlanId();
		const scenarioId = getScenarioId();
		if (isDefined(planId) && isDefined(scenarioId)) {
			push(buildRemoveIssuePath(planId, scenarioId));
		}
	};

	return (
		<ModalDialog shouldScrollInViewport onClose={toggleModalDialog} autoFocus={false}>
			<ModalHeader>
				<ModalTitle appearance="danger">{formatMessage(messages.removeFromPlanTitle)}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<span>
					<FormattedMessage
						{...messages.removeIssueFromPlanMessage}
						values={{
							removedIssuesPageLink: (
								<Button onClick={onClickRemovedIssue} appearance="link" spacing="none">
									{formatMessage(messages.removedIssueSettingsLinkMessage)}
								</Button>
							),
						}}
					/>
				</span>
			</ModalBody>
			<ModalFooter>
				<StandardButton appearance="subtle" onClick={toggleModalDialog}>
					{formatMessage(commonMessages.cancel)}
				</StandardButton>
				<StandardButton
					appearance="primary"
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						removeFromPlan();
						bulkActionSuccess(analyticsEvent);
					}}
				>
					{formatMessage(messages.removeFromPlanActionButton)}
				</StandardButton>
			</ModalFooter>
		</ModalDialog>
	);
};

export default RemoveIssuesDialog;
